import { useEffect, useState } from 'react'
import { PluginFactory } from './pluginFactory'
import PluginSwitchboard from './pluginSwitchboard'

import useToggle from './hooks/useToggle'
import Header from './components/header'
import Main from './components/main'
import Input from './components/input'
import { Menu } from './components/menu'
import goBackIcon from './assets/goBackIcon.svg'

import { v4 as uuidv4 } from 'uuid'
import { messageType, pingEndpoint, widgetId, widgetPluginsId } from './utils'
import { dataset_defaults } from './dataset_defaults'
import XIcon from './assets/xIcon'

const CHAT_ORIGIN_INVALID =
    'Widget not configured properly by website owner - data-chat_origin attribute cannot contain underscores or quotes. Widget can be used but conversations will have no location attached.'

let chat_origin = document.querySelector(widgetId)?.dataset?.chat_origin
if (chat_origin && chat_origin.match(/['"_]/)) {
    alert(CHAT_ORIGIN_INVALID)
}
const RESTRICTED_LETTERFILL_MSGS = 1
// Don't change this number casually otherwise the back/undo button's visiblity might be unpredicatable
// This is the number of intro bot messages to the letterfill and is used to detect when to disable the back button
// to disallow users from returning to the contentful flow from the rasa flow.
// Can also fix the back functionality from rasa to contentful instead.
// Note: If we ever change the letterfill intro, this will need to be updated.

// pluginFactory creates an instance of a plugin determined by dataset
const pluginFactory = new PluginFactory()

let switchboard
let sender_id

console.log('Hello from Widget.js')

const initSwitchboard = () => {
    if (document.querySelector(widgetPluginsId)) {
        switchboard = new PluginSwitchboard()
        let default_plugin = document.querySelector(widgetPluginsId).dataset.default_plugin || 'rasa_1'
        const persistentPlugin = sessionStorage.getItem('currentPlugin')
        if (persistentPlugin) default_plugin = persistentPlugin

        // currently only takes the last plugin...gotta figure out how to take multiple & passControl between them
        const pluginDivsData = Array.from(document.querySelector(widgetPluginsId).children).map(c => c.dataset)
        async function createPlugins() {
            pluginDivsData.forEach(pluginData => {
                ;(async () => {
                    const plugin = await pluginFactory.create({ ...pluginData })

                    switchboard.register(pluginData.plugin, plugin, default_plugin)
                })()
            })
        }

        // Check the status of the switchboard
        createPlugins()
            .then(() => {
                try {
                    switchboard.status()
                } catch (err) {
                    console.error(err)
                }
            })
            .then(async () => {
                try {
                    sender_id = await switchboard.generateSenderId(chat_origin)
                } catch (e) {
                    if (e.response.status === 403) {
                        console.error(e)
                        let killswitch = document.querySelector(widgetId)
                        killswitch.remove()
                        sessionStorage.clear()
                    } else {
                        throw e
                    }
                }
            })
    }
}

initSwitchboard()

const TRUE = 'true'

// MutationObserver config
const targetNode = document.querySelector(widgetId)
const mutationConfig = { attributes: true, childList: false, subtree: false, attributeFilter: ['data-external_open'] } // attributeOldValue: true }

export default function Widget({ GUI_dataset = null, styleEndpoint }) {
    const [config, setConfig] = useState(dataset_defaults)
    let {
        testing,
        external_control,
        big_bot_avatar_url,
        font,
        chat_bg_color,
        timeout,
        max_bot_response_delay_ms,
        use_inactive_popup,
        page_layout,
        initial_payload,
        notification_sound,
        chat_persistence,
        inactive_popup_text,
        widget_default_open,
    } = config
    const useInactivePopup = use_inactive_popup.toString() === 'true'
    const chatPersistence = chat_persistence === 'true'
    const pageLayout = page_layout === 'true'
    max_bot_response_delay_ms = Number(max_bot_response_delay_ms)
    timeout = Number(timeout)
    const externallyControlled = external_control === TRUE
    // get sessionStorage chatline history if exists
    const previousSession = JSON.parse(sessionStorage.getItem('chatlines')) ?? []
    // get soundOn toggle status and set as initial setting if exists
    let previousSound = sessionStorage.getItem('notifSound')
    let initSound = true
    previousSound === 'false' ? (initSound = false) : (initSound = true)
    let chatHistoryArray = JSON.parse(sessionStorage.getItem('chatHistory') || '[]')

    const [currentPlugin, setCurrentPlugin] = useState('')
    const [isActive, toggleIsActive] = useToggle(false)
    const [botTyping, toggleBotTyping] = useToggle(false)
    const [isExpanded, toggleIsExpanded] = useToggle(false)
    const [isMobile, toggleIsMobile] = useToggle(false)
    const [soundOn, toggleSoundOn] = useToggle(initSound)
    const [inputHidden, toggleInputHidden] = useToggle(false)
    const [usePageLayout, toggleUsePageLayout] = useToggle(pageLayout)
    const [showInactivePopup, toggleShowInactivePopup] = useToggle(false)
    const [isLoading, setIsLoading] = useState(true)
    const [hideWidget, setHideWidget] = useState(true)
    const [disableWidget, setDisableWidget] = useState(false)
    const [healthCheckStatus, setHealthCheckStatus] = useState([])
    const [menuActive, toggleMenuActive] = useToggle(false)
    const [animateCloseMenu, setAnimateCloseMenu] = useState(false)
    const [isRestarting, toggleIsRestarting] = useToggle(false)
    const [coverActive, toggleCoverActive] = useToggle(false)
    const [isSending, toggleIsSending] = useToggle(false)
    const [title, setTitle] = useState('')
    const [coverPageData, setCoverPageData] = useState(null)
    const [mainSizing, setMainSizing] = useState({ width: config.width, height: config.height, menuOverlayHeight: config.height })
    const [pageChatlines, setPageChatlines] = useState([])
    const [chatlines, setChatlines] = useState(previousSession)
    const [rasalines, setRasalines] = useState([])
    const [inRasaFlow, setInRasaFlow] = useState(false)

    useEffect(() => {
        const defaultOpen = config.widget_default_open === true
        if (defaultOpen) {
            setHideWidget(false)
            toggleIsActive(true)
        }
    }, [config.widget_default_open])

    // This is to observe data-external_open for changes, and keep the chatbot widget toggle state in sync with it
    useEffect(() => {
        if (externallyControlled) {
            const callback = function (mutationList, observer) {
                let { external_open } = mutationList[0].target.dataset
                if (isActive !== (external_open === TRUE)) {
                    toggleIsActive()
                }
            }
            const observer = new MutationObserver(callback)
            observer.observe(targetNode, mutationConfig)
            return () => {
                observer.disconnect() //clean up, so we don't accumulate observers with rerenders
            }
        }
    }, [isActive, GUI_dataset?.external_open]) // include GUI_dataset to pickup changes there in external_open - irrelevant in production, will always be null

    useEffect(() => {
        let newDataset = {
            testing: GUI_dataset !== null,
            ...dataset_defaults,
            // override defaults with SettingsGUIWrapper values/user settings
            ...(GUI_dataset ?? document.querySelector(widgetId)?.dataset),
        }
        setConfig(newDataset)

        // if config available, fetch it remotely
        const fetchConfig = async () => {
            let updatedConfig
            try {
                updatedConfig = await switchboard.fetchConfig(newDataset, dataset_defaults)
                setConfig(updatedConfig)
            } catch (e) {
                if (testing) console.log(`Dataset failed to load from django, using default dataset. Further details here: ${e}`)
            }
            // For some reason using toggleIsLoading breaks this...
            // it doesn't await the above setConfig so the cover page is never fetched.
            setIsLoading(false)
        }
        fetchConfig()
        setCurrentPlugin(switchboard?.currentPlugin())
        if (window.innerWidth < 720) {
            toggleIsMobile(true)
        }
        switchboard?.currentPlugin() === 'cover_page_plugin' && toggleInputHidden(true)
    }, [])

    useEffect(() => {
        setTimeout(function () {
            toggleShowInactivePopup(true)
        }, 2000)
    }, [])

    useEffect(() => {
        sessionStorage.removeItem('chatHistory')
    }, [])

    useEffect(() => {
        // to sort initial page / initial message send
        if (!isLoading && initial_payload && chatlines?.length < 1) {
            const sendOrFetchCoverPage = async () => {
                await send(null, initial_payload)
            }
            sendOrFetchCoverPage()
        }
        // to apply custom widget sizing after custom config loaded
        if (isExpanded) {
            let expandedSize = { ...mainSizing }
            expandedSize.width = '50vw'
            expandedSize.height = '80vh'
            expandedSize.menuOverlayHeight = '80vh'
            setMainSizing(expandedSize)
        } else if (isMobile) {
            let fullScreenSize = { ...mainSizing }
            fullScreenSize.width = 'calc(100% - 10px)'
            fullScreenSize.height = '100vh'
            fullScreenSize.menuOverlayHeight = '100vh'
            setMainSizing(fullScreenSize)
        } else {
            let widgetSize = { ...mainSizing }
            widgetSize.width = config.width
            widgetSize.height = `calc(${config.height} + 30px)`
            widgetSize.menuOverlayHeight = `calc(${config.height} + 121px)`
            setMainSizing(widgetSize)
        }
    }, [isLoading])

    useEffect(() => {
        if (chatPersistence) {
            //sessionStorage: string, so stringify to save and parse to load
            const stringChatlines = JSON.stringify(chatlines)
            sessionStorage.setItem('chatlines', stringChatlines)
            sessionStorage.setItem('senderId', sender_id)
            sessionStorage.setItem('currentPlugin', currentPlugin)
        }
    }, [chatlines])

    useEffect(() => {
        if (isExpanded) {
            let expandedSize = { ...mainSizing }
            expandedSize.width = '50vw'
            expandedSize.height = '80vh'
            expandedSize.menuOverlayHeight = '80vh'
            setMainSizing(expandedSize)
        } else if (isMobile) {
            let fullScreenSize = { ...mainSizing }
            fullScreenSize.width = 'calc(100% - 10px)'
            fullScreenSize.height = '100vh'
            fullScreenSize.menuOverlayHeight = '100vh'
            setMainSizing(fullScreenSize)
        } else {
            let widgetSize = { ...mainSizing }
            widgetSize.width = config.width
            widgetSize.height = `calc(${config.height} + 30px)`
            widgetSize.menuOverlayHeight = `calc(${config.height} + 121px)`
            setMainSizing(widgetSize)
        }
    }, [isExpanded])

    useEffect(() => {
        let calculatedHeight
        let updatedSizing = { ...mainSizing }
        if (document.getElementById('sbwidgetmain')) {
            document.getElementById('sbwidgetmain').style.transition = 'none'
        }

        if (inputHidden) {
            if (isExpanded) {
                calculatedHeight = 'calc(80vh + 30px)'
            } else if (isMobile) {
                calculatedHeight = '100vh'
            } else {
                calculatedHeight = `${parseInt(config.height) + 30}px`
            }
        } else {
            if (isExpanded) {
                calculatedHeight = 'calc(80vh - 30px)'
            } else if (isMobile) {
                calculatedHeight = '100vh'
            } else {
                calculatedHeight = `${parseInt(config.height) - 30}px`
            }
        }
        updatedSizing.height = calculatedHeight
        setMainSizing(updatedSizing)

        if (document.getElementById('sbwidgetmain')) {
            setTimeout(() => {
                document.getElementById('sbwidgetmain').style.transition = 'all 0.5s ease-in-out'
            }, 15)
        }
    }, [inputHidden])

    const notificationSound = () => {
        let sound = document.querySelector(widgetId)?.shadowRoot?.getElementById('audio')
        sound && sound.play()
    }

    const closeMenu = () => {
        setAnimateCloseMenu(true)
        setTimeout(() => {
            toggleMenuActive(false)
            setAnimateCloseMenu(false)
        }, 480)
    }

    const tryGoBack = () => {
        if (!isSending) {
            toggleBotTyping(true)
            switchboard.goBack(send)
        }
    }

    const addReplaceRasalines = (msg, payload, options) => {
        // We're trying to get all of the user and bot messages including, after, and the bot messages just before the
        // user message that starts the letterfill.
        // A series of bot messages start as a response from a user message of type contentful-button
        // The user message we detect for the letterfill has msg_type letterfill
        if (!msg) {
            // User message has no content, no need to add any lines
            return
        }
        if (options?.msg_type === 'contentful-button') {
            if (rasalines && rasalines[rasalines.length - 1]?.custom?.keyword === 'letterfill') {
                setRasalines(l => [...l, { message: msg, type: messageType.user }])
            }
        } else {
            setRasalines(l => [...l, { message: msg, type: messageType.user }])
        }
    }

    const updateRasalinesResponse = (options, response) => {
        if (rasalines && rasalines[rasalines.length - 1]?.custom?.keyword === 'letterfill') {
            setRasalines(l => [...l, response])
        } else if (options?.msg_type === 'contentful-button') {
            setRasalines(l => [response])
        } else if (options?.isFollowup === true || response.custom?.keyword === 'letterfill') {
            setRasalines(l => [...l, response])
        } else {
            testing && console.log('currently ignoring this response: ', response)
        }
    }

    const responseToChatline = response => {
        if (response.type === messageType.user) return response
        return {
            id: response.id,
            message: response.message,
            buttons: response.buttons ?? [],
            image: response.image ?? false,
            video: response.video ?? false,
            quicklink: response.quicklink ?? false,
            custom: response.custom ?? false,
            type: messageType.bot,
        }
    }
    const responseToPageChatline = (response, payload = '') => {
        if (response.type === messageType.user) return response
        return {
            id: response.id,
            message: response.message,
            buttons: response.buttons ?? [],
            image: response.image ?? false,
            video: response.video ?? false,
            quicklink: response.quicklink ?? false,
            title: response.title ?? false,
            custom: response.custom ?? false,
            payload: payload,
            type: messageType.bot,
        }
    }

    const send = async (msg, payload, options) => {
        if (options?.expectResponse === false) {
            // send a message that's effectively a side effect for which we don't care about the response
            await switchboard.processInput({ sender_id, message: msg, payload: payload, options }, timeout)
        } else {
            toggleIsSending(true)
            if (msg === 'undo') {
                let position = 0
                for (let i = 0; i < rasalines.length; i++) {
                    if (rasalines[rasalines.length - i - 1].type === messageType.user) {
                        position = -i - 1
                        break
                    }
                }
                setChatlines(l => rasalines.slice(0, position).map(response => responseToChatline(response)))
                setPageChatlines(l => rasalines.slice(0, position).map(response => responseToPageChatline(response)))
                setRasalines(rasalines.slice(0, position))

                toggleBotTyping(false)
                toggleIsSending(false)

                await switchboard.processInput({ sender_id, message: msg, payload: payload, options }, timeout)
                setCurrentPlugin(switchboard.currentPlugin())
                return
            }

            msg && setChatlines(l => [...l, { message: msg, type: messageType.user }])
            if (msg && usePageLayout) setPageChatlines(l => [...l, { message: msg, type: messageType.user }])
            addReplaceRasalines(msg, payload, options)

            msg && toggleBotTyping(true)
            let pluginResponse = await switchboard.processInput({ sender_id, message: msg, payload: payload, options }, timeout)
            setCurrentPlugin(switchboard.currentPlugin())
            if (Array.isArray(pluginResponse) && pluginResponse.some(response => response.custom?.keyword === 'letterfill')) {
                // we do this to check that we are in the letterflow.
                setInRasaFlow(true)
                // we clear chatlines in order to prevent the user from going back here as going back
                // beyond letterflow causes issues with the rasa model having a part finished form.
                setChatlines([])
            } else {
                setInRasaFlow(false)
            }
            if (testing) console.log(pluginResponse)
            if (!Array.isArray(pluginResponse) && pluginResponse?.coverpageComponent) {
                setCoverPageData(pluginResponse)
                toggleCoverActive(true)
            } else {
                const finalResponse = pluginResponse?.[pluginResponse.length - 1]
                if (finalResponse?.custom?.hideUserInput === true) {
                    toggleInputHidden(true)
                } else {
                    toggleInputHidden(false)
                }
                if (finalResponse?.custom?.pageLayout !== false) {
                    toggleUsePageLayout(false)
                    setPageChatlines([])
                } else {
                    toggleUsePageLayout(true)
                }
                if (!pluginResponse[0]?.custom?.isFollowUp) {
                    setTitle(pluginResponse[0]?.title)
                }
                const delay = Math.random() * max_bot_response_delay_ms
                pluginResponse.forEach((response, i) => {
                    setTimeout(() => {
                        setChatlines(l => [...l, responseToChatline(response)])
                        setPageChatlines(l => [...l, responseToPageChatline(response, payload)])
                        updateRasalinesResponse(options, response)

                        if (finalResponse?.followUpId) {
                            send('', finalResponse.followUpId, { isFollowup: true })
                        } else {
                            toggleBotTyping(false)
                            toggleIsSending(false)
                        }

                        soundOn && notificationSound()
                    }, (i + 1) * delay)
                })
            }
        }
    }

    const toggleSound = bool => {
        toggleSoundOn(bool)
        const notifSound = bool ? 'true' : 'false'
        sessionStorage.setItem('notifSound', notifSound)
    }

    const restart = () => {
        if (testing) console.log('reset conversation')
        const coverPageData = {
            title: sessionStorage.getItem('custom_cover_page_title'),
            text: sessionStorage.getItem('custom_cover_page_text'),
            button: sessionStorage.getItem('custom_cover_page_btn_text'),
        }
        sessionStorage.clear()
        sessionStorage.setItem('custom_cover_page_title', coverPageData.title)
        sessionStorage.setItem('custom_cover_page_text', coverPageData.text)
        sessionStorage.setItem('custom_cover_page_btn_text', coverPageData.button)
        sender_id = uuidv4()
        initSwitchboard()
        setChatlines([])
        setPageChatlines([])
        toggleIsRestarting(true)
        toggleCoverActive(false)
    }

    const handleActivateWidget = () => {
        toggleIsActive()
        // false below to only show up on initial load
        toggleShowInactivePopup(false)
    }

    const handleCloseInactivePopup = event => {
        event.stopPropagation()
        toggleShowInactivePopup()
    }

    useEffect(() => {
        if (isRestarting) {
            if (initial_payload) send(null, initial_payload)

            toggleIsRestarting(false)
        }
    }, [isRestarting])

    useEffect(() => {
        // if relevant for jest only, as left hand assignment expression
        if (document.querySelector(widgetId)) document.querySelector(widgetId).dataset.conversation_id = sender_id
    }, [sender_id])

    const endpointStates = {
        unknown: 'unknown',
        healthy: 'healthy',
        unhealthy: 'unhealthy',
    }

    const hideOrDisableWidgetOnHealthCheck = hideOrDisable => {
        if (config.hide_on_health_check_fail === 'true') {
            setHideWidget(hideOrDisable)
        } else if (config.hide_on_health_check_fail === 'false') {
            setDisableWidget(hideOrDisable)
            setHideWidget(false)
        } else {
            setDisableWidget(false)
            setHideWidget(false)
        }
    }

    useEffect(() => {
        if (config.health_check === 'false' || !config.health_check_endpoints) {
            hideOrDisableWidgetOnHealthCheck(false)
            return
        }
        let state = config.health_check_endpoints.split('||').map(endpoint => {
            return {
                endpoint: endpoint,
                state: endpointStates.unknown,
            }
        })
        if (state.length > 0) {
            hideOrDisableWidgetOnHealthCheck(true)
            setHealthCheckStatus(state)
        }
    }, [config])

    useEffect(() => {
        if (!healthCheckStatus || healthCheckStatus.length === 0) return
        const checkHealthStatus = (endpoint, state) => {
            setHealthCheckStatus(
                healthCheckStatus.map(ep => {
                    if (ep.endpoint === endpoint) return { ...ep, state: state }
                    else return ep
                })
            )
        }
        healthCheckStatus.forEach(ep => {
            if (ep.state === endpointStates.unknown) {
                pingEndpoint(
                    ep.endpoint,
                    () => checkHealthStatus(ep.endpoint, endpointStates.healthy),
                    () => checkHealthStatus(ep.endpoint, endpointStates.unhealthy)
                )
            }
        })
    }, [healthCheckStatus])

    useEffect(() => {
        if (!healthCheckStatus || healthCheckStatus.length === 0) return
        let healthy = healthCheckStatus.every(ep => ep.state === endpointStates.healthy)
        hideOrDisableWidgetOnHealthCheck(!healthy)
    }, [healthCheckStatus])

    if (isLoading) return <div></div>
    if (testing) console.log('switchboard status: ', switchboard.status())

    let showBackButton = false
    if (inRasaFlow && rasalines.filter(l => l.type === messageType.user).length !== RESTRICTED_LETTERFILL_MSGS) {
        showBackButton = true
    }

    if (!inRasaFlow && chatHistoryArray.length > 1) {
        showBackButton = true
    }

    return (
        <div
            style={{ fontFamily: font }}
            id='springbok_chatbot_widget_shadow'
        >
            {isActive ? (
                <div
                    className={'activeWidget ' + (isExpanded ? 'fullScreen' : '') + (isMobile ? 'mobile' : '')}
                    style={{ backgroundColor: chat_bg_color, pointerEvents: disableWidget ? 'none' : 'auto' }}
                >
                    {menuActive && (
                        <Menu
                            toggleMenuActive={toggleMenuActive}
                            config={config}
                            mainSizing={mainSizing}
                            soundOn={soundOn}
                            toggleSound={toggleSound}
                            restart={restart}
                            send={send}
                            animateCloseMenu={animateCloseMenu}
                            closeMenu={closeMenu}
                            isSending={isSending}
                            toggleIsActive={toggleIsActive}
                        />
                    )}
                    <Header
                        toggleIsActive={toggleIsActive}
                        restart={restart}
                        config={config}
                        mainSizing={mainSizing}
                        isMobile={isMobile}
                        isExpanded={isExpanded}
                        toggleIsExpanded={toggleIsExpanded}
                        menuActive={menuActive}
                        toggleMenuActive={toggleMenuActive}
                        closeMenu={closeMenu}
                        send={send}
                        sender_id={sender_id}
                    />
                    {coverPageData && coverActive && (
                        <coverPageData.coverpageComponent
                            toggleCoverActive={toggleCoverActive}
                            dataset={config}
                            coverOptions={coverPageData.options}
                            send={send}
                            mainSizing={mainSizing}
                        />
                    )}
                    <div
                        className='flexR titleHolder'
                        style={{ backgroundColor: chat_bg_color }}
                    >
                        {showBackButton && (
                            <img
                                src={goBackIcon}
                                className='backButton'
                                onClick={() => tryGoBack()}
                                alt='go back'
                            />
                        )}
                        {title && (
                            <div
                                className='title'
                                style={{ marginLeft: showBackButton ? '0px' : '42px' }}
                            >
                                {title ? title : chatlines[0]?.title}
                            </div>
                        )}
                    </div>
                    <Main
                        sender_id={sender_id}
                        chatlines={pageLayout ? pageChatlines : chatlines}
                        send={send}
                        botTyping={botTyping}
                        config={config}
                        isExpanded={isExpanded}
                        mainSizing={mainSizing}
                        currentPlugin={currentPlugin}
                        isSending={isSending}
                        scrollBarButtons={config.scrollBarButtons}
                        autoScrollDisabled={config.autoscroll_disabled}
                        title={title}
                    />
                    {!inputHidden && (
                        <Input
                            send={send}
                            isSending={isSending}
                            toggleBotTyping={toggleBotTyping}
                            setChatlines={setChatlines}
                            config={config}
                            disabled={disableWidget}
                            isExpanded={isExpanded}
                        />
                    )}
                </div>
            ) : (
                !externallyControlled &&
                !isLoading &&
                !hideWidget && (
                    <div>
                        {useInactivePopup && (
                            <button
                                onClick={handleActivateWidget}
                                className={`inactiveWidgetPopup ${showInactivePopup ? 'show' : 'hide'}`}
                                style={{
                                    borderColor: config.inactive_popup_border_color,
                                    backgroundColor: config.inactive_popup_bg_color,
                                    color: config.inactive_popup_text_color,
                                }}
                            >
                                <a onClick={handleCloseInactivePopup}>
                                    <XIcon
                                        className='inactiveWidgetPopupClose'
                                        stroke={config.inactive_popup_close_color}
                                    />
                                </a>
                                {inactive_popup_text}
                            </button>
                        )}
                        <button
                            className='inactiveWidgetButton'
                            onClick={handleActivateWidget}
                            style={{ backgroundColor: config.chat_bg_color, border: `2px solid ${config.header_bg_color}` }}
                        >
                            <img
                                src={big_bot_avatar_url}
                                alt='bot icon'
                            />
                        </button>
                    </div>
                )
            )}
            <audio
                id='audio'
                src={notification_sound}
            />
            {styleEndpoint && <style type='text/css'>@import url({styleEndpoint});</style>}
        </div>
    )
}
